<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6">

    <!-- GRV Form Card -->
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-1 flex items-center justify-between gap-3">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Goods Receiving Voucher (GRV)</h2>
        <div class="relative print-hide shrink-0" ref="noReceiptDropdownRef">
          <button
            type="button"
            @click.stop="toggleNoReceiptDropdown"
            class="px-3 py-1.5 text-[0.78rem] font-semibold text-[#0d4169] border border-[#cbd5e1] rounded-md bg-white hover:bg-slate-50 transition-all whitespace-nowrap"
          >
            Has No Reciept GRVs
            <span v-if="noReceiptGrvList.length" class="ml-1 text-slate-500">({{ noReceiptGrvList.length }})</span>
          </button>
          <div
            v-if="noReceiptDropdownOpen"
            class="absolute right-0 top-full mt-1 z-50 w-72 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
            @click.stop
          >
            <div class="px-3 py-2 border-b border-slate-100 text-[0.72rem] font-bold text-slate-600 uppercase tracking-wide">
              GRVs Without Receipt
            </div>
            <div v-if="loadingNoReceiptGrvs" class="px-3 py-4 text-center text-sm text-slate-500">
              Loading...
            </div>
            <div v-else-if="!noReceiptGrvList.length" class="px-3 py-4 text-center text-sm text-slate-400 italic">
              No GRVs without receipt
            </div>
            <div v-else class="max-h-60 overflow-y-auto">
              <label
                v-for="row in noReceiptGrvList"
                :key="row.grvNo"
                class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-b-0"
              >
                <input
                  v-model="selectedNoReceiptGrvs"
                  type="checkbox"
                  :value="row.grvNo"
                  class="w-4 h-4 accent-[#0d4169] cursor-pointer shrink-0"
                >
                <span class="text-sm font-semibold text-slate-700">{{ row.grvNo }}</span>
              </label>
            </div>
            <div class="border-t border-slate-100 px-3 py-2 flex justify-end">
              <button
                type="button"
                @click="saveNoReceiptGrvs"
                :disabled="savingNoReceiptGrvs || !selectedNoReceiptGrvs.length"
                class="px-4 py-1.5 bg-[#0d4169] text-white text-xs font-bold rounded-md hover:bg-[#0a3354] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ savingNoReceiptGrvs ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <form @submit.prevent="processGrv" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">

          <!-- GRV Number (display-only) -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Next GRV Number</label>
            <input
              :value="grvNumberDisplay || '-'"
              type="text"
              readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed"
            >
          </div>

          <!-- Transaction Date -->
         <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Transaction Date</label>
            <input v-model="form.transactionDate" type="date"
              :min="(isPendingEditMode && canEditTransactionDate) ? null : todayDate"
              :max="todayDate"
              :disabled="isPendingEditMode && !canEditTransactionDate"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
          </div>

          <!-- PR Number -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">PR Number</label>
            <input v-model="form.prNumber" type="text" placeholder="e.g. PR-20240001"
              :disabled="isPendingEditMode && !canEditPrNumber"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
          </div>

          <!-- Supplier -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Supplier</label>
            <select v-model="form.supplierId"
              :disabled="(isPendingEditMode && !canEditSupplier)"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" required>
              <option value="" disabled>Select Supplier</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>

          <!-- Category -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Category</label>
            <select v-model="form.categoryId"
              :disabled="(isPendingEditMode && !canEditCategory)"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" required>
              <option value="" disabled>Select Category</option>
              <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>

          <!-- Item -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Item</label>
            <select v-model="form.selectedItemKey"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              :disabled="!form.categoryId || (isPendingEditMode && !canEditItemField)"
              required>
              <option value="" disabled>Select Item</option>
              <option
                v-for="opt in filteredSelectableItems"
                :key="opt.key"
                :value="opt.key"
                :style="opt.type === 'product' ? 'color:#16a34a; font-weight:600;' : ''"
              >
                {{ opt.name }} ({{ opt.unit }})
              </option>
            </select>
          </div>

          <!-- Item Specifications: Quantity / Unit Price / Variant (per measurement) -->
          <div class="col-span-2 md:col-span-3 lg:col-span-2 w-full min-w-0 flex flex-col gap-1">
            <div class="text-[0.78rem] font-semibold text-[#1e293b] text-left">
              Item Specifications
              <!-- <span v-if="selectedUnit" class="text-xs font-normal text-slate-500">
                &mdash; define quantity, unit price and variant for this {{ selectedUnit }}.
              </span> -->
            </div>
            <div class="w-full min-w-0" style="border: 1px solid #cbd5e1; border-radius: 6px; padding: 2px; background-color: #fdfdfd; position: relative;" >
              <div class="flex flex-col gap-1">
                <div
                  v-for="(entry, idx) in variantEntries"
                  :key="idx"
                  class="relative grid grid-cols-2 gap-1 sm:grid-cols-4 border border-slate-100 rounded-lg shadow-sm"
                >
                  <div class="flex flex-col gap-1">
                    <label v-if="idx === 0" class="text-[0.7rem] font-semibold text-slate-500">Qty Received</label>
                    <input
                      v-model.number="entry.quantity"
                      type="number"
                      min="0"
                         step="any"
                      inputmode="decimal"
                      placeholder="0"
                      :disabled="isPendingEditMode && !canEditQtyAt(idx)"
                      class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs"
                      required
                    />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label v-if="idx === 0" class="text-[0.7rem] font-semibold text-slate-500">Unit Price</label>
                    <input
                      v-model.number="entry.unitPrice"
                      type="number"
                      min="0"
                      step="any"
                      inputmode="decimal"
                      placeholder="0.00"
                      :disabled="isPendingEditMode"
                      class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs"
                      required
                    />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label v-if="idx === 0" class="text-[0.7rem] font-semibold text-slate-500">Volume ( <span class="text-red-500">{{ selectedUnit || '' }}</span>)</label>
                    <input
                      v-model="entry.variant"
                      type="number"
                      min="0"
                         step="any"
                      inputmode="decimal"
                      placeholder="0"
                      :disabled="isPendingEditMode && !canEditVolumeAt(idx)"
                      class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs"
                      required
                    />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label v-if="idx === 0" class="text-[0.7rem] font-semibold text-slate-500">Expiry Date</label>
                    <input
                      v-model="entry.expiryDate"
                      type="date"
                      :min="todayDate"
                      :disabled="isPendingEditMode && !canEditExpiryAt(idx)"
                      class="w-full px-1.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs"
                      required
                    />
                  </div>
                  
                  <button
                    v-if="variantEntries.length > 1 && !isPendingEditMode"
                    type="button"
                    @click="removeVariantEntry(idx)"
                    class="absolute -top-1.5 -right-1.5 h-4 w-4 flex items-center justify-center bg-red-500 text-white rounded-full text-[10px] shadow-md border border-white hover:bg-red-600 transition-colors"
                  >
                    -
                  </button>
                </div>
              </div>
              
              <button
                v-if="!isPendingEditMode"
                type="button"
                @click="addVariantEntry"
                class="mr-1 absolute -bottom-1.5 -right-1.5 h-4 w-4 flex items-center justify-center bg-green-600 text-white rounded-full text-[10px] font-bold shadow-lg border border-white hover:bg-green-700 transition-transform active:scale-90 z-10"
              >
                +
              </button>
            </div>
          </div>

          <!-- Total Price + Is Taxable + Pending Update Error (one row) -->
          <div class="col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-4">
            <div class="flex items-start gap-4">
              <!-- Total Price -->
              <div class="flex flex-col gap-1 shrink-0 w-44">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Total Price</label>
                <input
                  :value="totalPrice.toFixed(2)"
                  type="text"
                  readonly
                  class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed"
                >
              </div>

              <!-- Is Taxable / Has Receipt -->
              <div class="flex items-center gap-4 pt-6 shrink-0 flex-wrap">
                <div class="flex items-center gap-2">
                  <input
                    id="isTaxable"
                    v-model="form.isTaxable"
                    type="checkbox"
                    :disabled="isPendingEditMode && !canEditTaxable"
                    class="w-4 h-4 accent-[#0d4169] cursor-pointer"
                  >
                  <label for="isTaxable" class="text-[0.78rem] font-semibold text-[#1e293b] cursor-pointer whitespace-nowrap">
                    Is Taxable Item?
                  </label>
                </div>
              </div>

              <!-- Pending update error -->
              <div v-if="pendingUpdateError" class="flex-1 min-w-0 pt-6">
                <span class="block w-full text-[0.72rem] font-semibold text-red-600 whitespace-pre-wrap break-words">
                  {{ pendingUpdateError }}
                </span>
              </div>
            </div>
          </div>

        </div>

        <!-- Action Buttons -->
        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-end items-center gap-3">
          <div v-if="isPendingEditMode" class="flex items-center gap-2 mr-auto">
            <input
              id="batchHasReceiptPending"
              v-model="batchHasReceipt"
              type="checkbox"
              class="w-4 h-4 accent-[#0d4169] cursor-pointer"
            >
            <label for="batchHasReceiptPending" class="text-[0.78rem] font-semibold text-[#1e293b] cursor-pointer whitespace-nowrap">
              Has Reciept?
            </label>
          </div>
          <button
            v-if="isEditing"
            type="button"
            @click="cancelEdit"
            class="px-5 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95"
          >
            {{ isEditing ? 'Update GRV' : 'Add to List' }}
          </button>
        </div>
      </form>
    </div>

    <!-- GRV List Table (only shown when records exist) -->
    <div v-if="pendingFlattenedGrvList.length > 0" class="flex flex-col gap-2">
      <div class="print-hide w-full flex justify-between items-center mb-2 px-2">
        <h3 class="text-[0.8rem] font-bold text-slate-600">Pending Edit Access (GRV)</h3>
      </div>
      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table items-to-receive-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Grv No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Pr No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Supplier</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Expiry</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Taxable</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in pendingFlattenedGrvList" :key="`pending-${index}`" class="hover:bg-slate-100/50 transition-colors border-b grv-large-row">
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">{{ index + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">{{ row.grvNumber }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">{{ row.prNumber || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700 font-semibold">{{ row.supplierName }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">{{ row.categoryName }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700 font-bold text-[#0d4169]">{{ row.itemName }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700 font-bold">{{ row.displayQuantity }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">{{ row.displayVariant }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">{{ row.displayUnitPrice }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700 font-bold">{{ row.displayTotalPrice }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">{{ row.displayExpiry }}</td>
              <td class="px-2 py-1.5 text-[0.8rem] text-slate-700">
                <span class="status-pill text-[0.6rem]" :class="row.isTaxable ? 'active' : 'inactive'">
                  {{ row.isTaxable ? 'Yes' : 'No' }}
                </span>
              </td>
              <td v-if="row.isPrimaryRow" :rowspan="row.groupSize" class="px-2 py-1.5 text-[0.8rem] text-slate-700 print-hide bg-white border-b">
                <div class="flex items-center gap-1">
                  <button @click="editPendingGrv(row.originalIndex)" class="editbtn hover:underline text-emerald-600">Edit</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Other Transfer Pending Items -->
    <div v-if="flattenedOtherTransferPending.length > 0" class="flex flex-col gap-2 print-hide w-full min-w-0">
      <div class="w-full flex justify-between items-center mb-2 px-2">
        <h3 class="text-[0.8rem] font-bold text-slate-600">Other Transfer Pending Items</h3>
      </div>
      <div class="w-full max-w-full overflow-x-auto shadow-sm rounded-xl other-transfer-pending-scroll">
        <table class="other-transfer-pending-table border-collapse bg-white">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Siv No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Date</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">From</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Merging Action</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in flattenedOtherTransferPending"
              :key="`otp-${row.itemRowId}-${index}`"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ index + 1 }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.sivNo }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.date }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-semibold">{{ row.from }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-bold text-[#0d4169]">{{ row.itemName }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.uom || '-' }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-bold">{{ row.displayQuantity }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.displayUnitPrice }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-bold">{{ row.displayTotalPrice }}</td>
              <td
                v-if="row.isPrimaryRow"
                :rowspan="row.groupSize"
                class="px-2 py-1.5 text-sm text-slate-700 bg-white border-b align-middle other-transfer-merge-cell"
              >
                <div class="other-transfer-merge-actions">
                  <select
                    :value="getOtherTransferMerge(row.itemRowId).categoryId"
                    class="other-transfer-merge-select px-2 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs bg-white cursor-pointer"
                    @change="onOtherTransferMergeCategoryChange(row.itemRowId, $event.target.value)"
                  >
                    <option value="" disabled>Select Category</option>
                    <option v-for="cat in selectableCategories" :key="`otp-cat-${cat.id}`" :value="cat.id">{{ cat.name }}</option>
                  </select>
                  <select
                    :value="getOtherTransferMerge(row.itemRowId).selectedItemKey"
                    class="other-transfer-merge-select px-2 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs bg-white cursor-pointer"
                    :disabled="!getOtherTransferMerge(row.itemRowId).categoryId"
                    @change="onOtherTransferMergeItemChange(row.itemRowId, $event.target.value)"
                  >
                    <option value="" disabled>Select Item</option>
                    <option
                      v-for="opt in getOtherTransferMergeSelectableItems(row.itemRowId)"
                      :key="`otp-item-${row.itemRowId}-${opt.key}`"
                      :value="opt.key"
                      :style="opt.type === 'product' ? 'color:#16a34a; font-weight:600;' : ''"
                    >
                      {{ opt.name }} ({{ opt.unit }})
                    </option>
                  </select>
                </div>
              </td>
              <td
                v-if="row.isSivApprovePrimary"
                :rowspan="row.sivApproveGroupSize"
                class="px-2 py-1.5 text-sm text-slate-700 bg-white border-b align-middle"
              >
                <button
                  type="button"
                  class="px-3 py-1 bg-emerald-600 text-white text-xs font-bold rounded-md hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="isApprovingOtherTransfer"
                  @click="approveOtherTransferPending(row.otherSivId, row.sivNo)"
                >
                  Approve
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="grvList.length > 0" class="flex flex-col gap-2 ">

      <div class="print-hide w-full flex justify-between items-center mb-2 px-2">
        <h3 class="text-[0.8rem] font-bold text-slate-600">Items to Receive</h3>
      </div>
      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <!-- Print Header (Hidden unless printing) -->
        <div class="hidden print:block mb-6">
          <div class="print-doc-header print-doc-header-grid">
            <div class="print-doc-left">
              <div class="print-company-block">
                <img
                  v-if="printCompanyLogo"
                  :src="printCompanyLogo"
                  :alt="printCompanyName || 'Company Logo'"
                  class="print-company-logo"
                />
                <div v-else class="print-company-logo-fallback">Logo</div>
              </div>
            </div>
            <div class="print-doc-title-wrap">
              <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
              <h1 class="print-doc-title text-xl font-bold text-center mb-2">Goods Receiving Voucher</h1>
            </div>
            <div class="print-doc-meta print-doc-right">
              <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
            </div>
          </div>
          <hr class="my-4 border-black" />
        </div>

        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Grv No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Pr No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Supplier</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Expiry</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Taxable</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in flattenedGrvList" :key="index" class="hover:bg-slate-100/50 transition-colors border-b grv-large-row">
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ index + 1 }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.grvNumber }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.prNumber || '-' }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-semibold">{{ row.supplierName }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.categoryName }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-bold text-[#0d4169]">{{ row.itemName }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-bold">{{ row.displayQuantity }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.displayVariant }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.displayUnitPrice }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700 font-bold">{{ row.displayTotalPrice }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">{{ row.displayExpiry }}</td>
              <td class="px-2 py-1.5 text-sm text-slate-700">
                <span class="status-pill text-[0.6rem]" :class="row.isTaxable ? 'active' : 'inactive'">
                  {{ row.isTaxable ? 'Yes' : 'No' }}
                </span>
              </td>
              <td v-if="row.isPrimaryRow" :rowspan="row.groupSize" class="px-2 py-1.5 text-sm text-slate-700 print-hide bg-white border-b">
                <div class="flex items-center gap-1">
                  <button  @click="editGrv(row.originalIndex)" class=" editbtn hover:underline text-emerald-600">Edit</button>
                  <button  @click="deleteGrv(row.originalIndex)" class="delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                   Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- Print Footer (Hidden unless printing) -->
        <div class="hidden print:block mt-8 w-full">
           <div class="flex flex-col gap-1.5 pl-8">
             <div class="flex items-baseline gap-2 text-sm">
               <span>Base Amount:</span>
               <span class="font-bold">{{ baseTotal.toFixed(2) }}</span>
             </div>
             <div class="flex items-baseline gap-2 text-sm">
               <span>Taxable Amount:</span>
               <span class="font-bold">{{ taxableAmountTotal.toFixed(2) }}</span>
             </div>
             <div class="flex items-baseline gap-2 text-sm">
               <span>Tax (15%):</span>
               <span class="font-bold">{{ taxTotal.toFixed(2) }}</span>
             </div>
             <div class="flex items-baseline gap-2 text-sm border-t border-black pt-1 mt-1">
               <span class="font-black text-[#0d4169]">Grand Total:</span>
               <span class="font-black text-base text-[#0d4169]">{{ grandTotal.toFixed(2) }}</span>
             </div>
           </div>
           
           <div class="mt-16 flex justify-between px-10 text-sm">
              <div class="text-center">
                <div class="border-b border-black w-40 mb-2"></div>
                <strong>Received By</strong>
              </div>
              <div class="text-center">
                <div class="border-b border-black w-40 mb-2"></div>
                <strong>Checked By</strong>
              </div>
              <div class="text-center">
                <div class="border-b border-black w-40 mb-2"></div>
                <strong>Approved By</strong>
              </div>
           </div>
        </div>
      </div>

      <!-- Action Buttons (Summary Footer) -->
      <div class="print-hide flex justify-between items-end px-4 mt-6 mb-10">
        <div class="flex flex-col gap-2">
          <div class="flex items-baseline gap-2">
            <span class="text-[0.7rem] text-slate-500 font-bold uppercase tracking-wider">Base Amount:</span>
            <span class="text-sm font-bold text-slate-800">{{ baseTotal.toFixed(2) }}</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-[0.7rem] text-slate-500 font-bold uppercase tracking-wider">Taxable Amount:</span>
            <span class="text-sm font-bold text-slate-800">{{ taxableAmountTotal.toFixed(2) }}</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-[0.7rem] text-slate-500 font-bold uppercase tracking-wider">Tax (15%):</span>
            <span class="text-sm font-bold text-slate-800">{{ taxTotal.toFixed(2) }}</span>
          </div>
          <div class="flex items-baseline gap-2 pt-1.5 border-t border-slate-200 mt-1">
            <span class="text-[0.7rem] text-[#0d4169] font-black uppercase tracking-wider leading-none">Grand Total:</span>
            <span class="text-lg font-black text-[#0d4169] leading-none">{{ grandTotal.toFixed(2) }}</span>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <div class="flex items-center gap-2">
            <input
              id="batchHasReceipt"
              v-model="batchHasReceipt"
              type="checkbox"
              class="w-4 h-4 accent-[#0d4169] cursor-pointer"
            >
            <label for="batchHasReceipt" class="text-[0.78rem] font-semibold text-[#1e293b] cursor-pointer whitespace-nowrap">
              Has Reciept?
            </label>
          </div>
          <button
            @click="saveToDatabase"
            :disabled="isSaving"
            class="min-w-[100px] px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
          >
        <div
  v-if="isSaving"
  class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
></div>
          <span v-if="!isSaving">Save GRV</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

// Dropdown data
const suppliers = ref([])
const categories = ref([])
const registeredItems = ref([])
const products = ref([])
const departments = ref([])

// UI State
const loading = ref(false)
const isSaving = ref(false)
const isApprovingOtherTransfer = ref(false)
const isEditing = ref(false)
const editIndex = ref(-1)
const editSource = ref(null) // 'items' | 'pending' | null
const pendingEditId = ref(null)
const pendingEditIndex = ref(-1)
const pendingEditPermissions = ref(null)
const pendingUpdateError = ref('')

// Display-only GRV number fetched from backend.
// This is intentionally NOT used in the save/update payload.
const grvNumberDisplay = ref('')
const batchHasReceipt = ref(false)

// Has No Receipt GRV dropdown
const noReceiptDropdownRef = ref(null)
const noReceiptDropdownOpen = ref(false)
const noReceiptGrvList = ref([])
const selectedNoReceiptGrvs = ref([])
const loadingNoReceiptGrvs = ref(false)
const savingNoReceiptGrvs = ref(false)

watch(pendingUpdateError, (msg) => {
  if (!msg) return
  const keepGrvNumber = form.value?.grvNumber || ''
  form.value = { ...getInitialForm(), grvNumber: keepGrvNumber }
  variantEntries.value = [{ quantity: null, unitPrice: null, variant: '', expiryDate: '' }]
})

// Form state
const getInitialForm = () => ({
  grvNumber: '',
  transactionDate: new Date().toISOString().slice(0, 10),
  prNumber: '',
  supplierId: '',
  categoryId: '',
  selectedItemKey: '',
  itemType: 'item', // 'item' | 'product'
  itemId: null,
  productId: null,
  quantity: 0,
  unitPrice: 0,
  totalPrice: 0,
  expiryDate: '',
  isTaxable: true,
})

const form = ref(getInitialForm())
const variantEntries = ref([{ quantity: null, unitPrice: null, variant: '', expiryDate: '' }])
const todayDate = new Date().toISOString().slice(0, 10)

function vatRateForTaxable(isTaxable) {
  return isTaxable ? 15 : 0
}

// Context
const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

function parseAssignedCategoryIds(raw) {
  if (!raw) return []
  return String(raw)
    .split(',')
    .map(v => Number(String(v).trim()))
    .filter(v => Number.isInteger(v) && v > 0)
}

// Select Category options: All:true + empty category_ids → all active categories;
// otherwise restrict to the user's assigned store category_ids (same csv format as UserModal).
const selectableCategories = computed(() => {
  const list = Array.isArray(categories.value) ? categories.value : []
  const user = currentUser.value
  let privilege = {}
  const rawPriv = user?.privilege
  if (rawPriv) {
    try {
      privilege = typeof rawPriv === 'string' ? JSON.parse(rawPriv) : rawPriv
    } catch {
      privilege = {}
    }
  }
  const hasAll = privilege?.All === true || privilege?.all === true
  const assignedIds = parseAssignedCategoryIds(user?.category_ids || user?.categoryIds)

  if (hasAll && assignedIds.length === 0) {
    return list
  }

  if (assignedIds.length === 0) {
    // No store categories assigned and not All:true → keep prior unrestricted list
    // so existing users without category_ids are unaffected.
    return list
  }

  const idSet = new Set(assignedIds)
  return list.filter(c => idSet.has(Number(c.id)))
})

// Computed
const totalPrice = computed(() =>
  variantEntries.value.reduce(
    (sum, entry) => sum + (Number(entry.quantity || 0) * Number(entry.unitPrice || 0)),
    0
  )
)

const filteredSelectableItems = computed(() => {
  const selectedKey = String(form.value.selectedItemKey || '')
  const [selectedType = '', selectedIdStr = ''] = selectedKey.split(':')
  const selectedProductId = selectedType === 'product' ? Number(selectedIdStr) : null
  const selectedItemId = selectedType === 'item' ? Number(selectedIdStr) : null
  const hasCategory = !!form.value.categoryId
  const catId = hasCategory ? Number(form.value.categoryId) : null
  const selectedProduct =
    Number.isFinite(selectedProductId) && selectedProductId > 0
      ? products.value.find(p => Number(p.id) === selectedProductId)
      : null
  const selectedItem =
    Number.isFinite(selectedItemId) && selectedItemId > 0
      ? registeredItems.value.find(i => Number(i.id) === selectedItemId)
      : null

  // Pending edit safety: if category is missing, still show currently selected row.
  if (!hasCategory) {
    if (selectedProduct) {
      return [{
        key: `product:${selectedProduct.id}`,
        type: 'product',
        id: selectedProduct.id,
        name: selectedProduct.name,
        unit: selectedProduct.uom ?? selectedProduct.UOM ?? selectedProduct.unit ?? selectedProduct.unitProduction ?? selectedProduct.u_o_m ?? '',
        unitProduction: selectedProduct.uom ?? selectedProduct.UOM ?? selectedProduct.unit ?? selectedProduct.unitProduction ?? selectedProduct.u_o_m ?? '',
      }]
    }
    if (selectedItem) {
      return [{
        key: `item:${selectedItem.id}`,
        type: 'item',
        id: selectedItem.id,
        name: selectedItem.itemName,
        unit: selectedItem.unit,
        unitProduction: selectedItem.unitProduction,
      }]
    }
    return []
  }

  const itemOptions = registeredItems.value
    .filter(item => Number(item.categoryId) === catId)
    .map(item => ({
      key: `item:${item.id}`,
      type: 'item',
      id: item.id,
      name: item.itemName,
      unit: item.unit,
      unitProduction: item.unitProduction,
    }))

  const activeProducts = products.value.filter(p => (p.status ?? 1) === 1)
  const productsForOptions = selectedProduct
    ? (activeProducts.some(p => Number(p.id) === Number(selectedProduct.id))
        ? activeProducts
        : [...activeProducts, selectedProduct])
    : activeProducts

  const productOptions = productsForOptions
    .filter(p => {
      const pCat = p.categoryId ?? p.category_id ?? p.category?.id ?? null
      return pCat == null ? true : Number(pCat) === catId
    })
    .map(p => ({
      key: `product:${p.id}`,
      type: 'product',
      id: p.id,
      name: p.name,
      unit: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? p.u_o_m ?? '',
      unitProduction: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? p.u_o_m ?? '',
    }))

  return [...itemOptions, ...productOptions].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
  )
})

const selectedItem = computed(() => {
  const key = String(form.value.selectedItemKey || '')
  if (!key) return null
  const [type, idStr] = key.split(':')
  const id = Number(idStr)
  if (type === 'product') {
    const p = products.value.find(x => Number(x.id) === id)
    if (!p) return null
    return {
      type: 'product',
      id: p.id,
      name: p.name,
      unit: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? p.u_o_m ?? '',
      unitProduction: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? p.u_o_m ?? '',
    }
  }
  const it = registeredItems.value.find(x => Number(x.id) === id)
  if (!it) return null
  return {
    type: 'item',
    id: it.id,
    name: it.itemName,
    unit: it.unit,
    unitProduction: it.unitProduction,
  }
})

const selectedUnit = computed(() => {
  const sel = selectedItem.value
  if (!sel) return ''

  // For products, show Volume unit based on product UOM
  if (sel.type === 'product') {
    const u = String(sel.unitProduction || sel.unit || '').trim()
    if (u === 'K.G.') return 'Gram'
    if (u === 'liter') return 'M.L.'
    if (u === 'Pieces') return 'Pieces'
    return u
  }
if(sel.unitProduction === 'Glass' && sel.unit === 'K.G.') {
  return 'Gramm'
}

if(sel.unitProduction === 'Glass' && sel.unit === 'liter') {
  return 'M.L.'
}

  

  // For items, keep existing behavior
  return sel.unitProduction || ''
})

// GRV list (Temporary table)
const grvList = ref([])
const pendingGrvList = ref([])
const pendingFlattenedGrvList = ref([])
const otherTransferPendingItems = ref([])
const otherTransferMergeByItemId = ref({})

const baseTotal = computed(() => 
  grvList.value.reduce((sum, item) => sum + (item.totalPrice || 0), 0)
)

const taxTotal = computed(() => 
  grvList.value.reduce((sum, item) => {
    if (item.isTaxable) {
      return sum + (item.totalPrice * 0.15)
    }
    return sum
  }, 0)
)

const taxableAmountTotal = computed(() => 
  grvList.value.reduce((sum, item) => item.isTaxable ? sum + (item.totalPrice || 0) : sum, 0)
)

const grandTotal = computed(() => baseTotal.value + taxTotal.value)
const isPendingEditMode = computed(() => isEditing.value && editSource.value === 'pending')

const canEditTransactionDate = computed(() =>
  !isPendingEditMode.value || !!pendingEditPermissions.value?.transactionDate
)
const canEditPrNumber = computed(() =>
  !isPendingEditMode.value || !!pendingEditPermissions.value?.prNo
)
const canEditSupplier = computed(() =>
  !isPendingEditMode.value || !!pendingEditPermissions.value?.supplier
)
const canEditCategory = computed(() =>
  !isPendingEditMode.value || !!pendingEditPermissions.value?.category
)
const canEditItemField = computed(() =>
  !isPendingEditMode.value || !!pendingEditPermissions.value?.itemField
)
const canEditTaxable = computed(() =>
  !isPendingEditMode.value || !!pendingEditPermissions.value?.taxable
)


// Computed flattened representation for visualization table
const flattenedGrvList = computed(() => {
  const list = []
  grvList.value.forEach((grv, index) => {
    // Determine arrays of attributes
    const qs = (grv.quantityDetails || String(grv.quantity || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    const ps = (grv.unitPriceDetails || String(grv.unitPrice || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    const vs = (grv.variantDetails || String(grv.variant || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    const es = (grv.expiryDateDetails || String(grv.expiryDate || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    
    // Find the max length of splits. Usually they match if they used the + button. If not, default to 1.
    const maxLen = Math.max(1, qs.length, ps.length, vs.length, es.length)
    
    if (qs.length === 0) {
      // Empty edge case safeguard
      list.push({
        ...grv,
        originalIndex: index,
        isPrimaryRow: true,
        displayQuantity: '-',
        displayVariant: '-',
        displayUnitPrice: '-',
        displayTotalPrice: '0.00',
        displayExpiry: '-',
        supplierName: getSupplierName(grv.supplierId),
        categoryName: getCategoryName(grv.categoryId),
        itemName: getSelectableName(grv.itemType, grv.itemType === 'product' ? grv.productId : grv.itemId)
      })
      return
    }

    // Split them out line by line
    for (let i = 0; i < maxLen; i++) {
        const rowQty = qs[i] || '0'
        const rowPrice = ps[i] || '0'
        const rowTotal = (Number(rowQty) * Number(rowPrice)).toFixed(2)
        
        list.push({
          ...grv,
          originalIndex: index, // Track original object index for editing & deletion mapping
          isPrimaryRow: i === 0, // Primary controls row
          
          displayQuantity: rowQty,
          displayUnitPrice: rowPrice,
          displayVariant: vs[i] || '-',
          displayExpiry: es[i] || '-',
          displayTotalPrice: rowTotal,
          groupSize: maxLen,
          
          // Duplicate meta tags for all split rows as requested
          grvNumber: grv.grvNumber,
          prNumber: grv.prNumber,
          supplierName: getSupplierName(grv.supplierId),
          categoryName: getCategoryName(grv.categoryId),
          itemName: getSelectableName(grv.itemType, grv.itemType === 'product' ? grv.productId : grv.itemId),
          isTaxable: grv.isTaxable
        })
    }
  })
  
  return list
})

const pendingFlatRows = computed(() => {
  const list = []
  pendingGrvList.value.forEach((grv, index) => {
    const qs = (grv.quantityDetails || String(grv.quantity || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    const ps = (grv.unitPriceDetails || String(grv.unitPrice || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    const vs = (grv.variantDetails || String(grv.variant || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    const es = (grv.expiryDateDetails || String(grv.expiryDate || '')).split(',').map(s => s.trim()).filter(s => s !== '')
    const maxLen = Math.max(1, qs.length, ps.length, vs.length, es.length)

    for (let i = 0; i < maxLen; i++) {
      const rowQty = qs[i] || '0'
      const rowPrice = ps[i] || '0'
      const rowTotal = (Number(rowQty) * Number(rowPrice)).toFixed(2)
      list.push({
        ...grv,
        originalIndex: index,
        isPrimaryRow: i === 0,
        groupSize: maxLen,
        displayQuantity: rowQty,
        displayUnitPrice: rowPrice,
        displayVariant: vs[i] || '-',
        displayExpiry: es[i] || '-',
        displayTotalPrice: rowTotal,
        supplierName: getSupplierName(grv.supplierId),
        categoryName: getCategoryName(grv.categoryId),
        itemName: getSelectableName(grv.itemType, grv.itemType === 'product' ? grv.productId : grv.itemId),
        isTaxable: grv.isTaxable
      })
    }
  })
  return list
})

// Data Fetching
async function loadDropdownData() {
  if (!companyId.value && !branchId.value) return

  loading.value = true
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    
    const [supRes, catRes, itemRes, prodRes, depRes] = await Promise.all([
      apiFetch(branchId.value ? `/suppliers/branch/${branchId.value}` : `/suppliers/company/${companyId.value}`),
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/products?${query}&activeOnly=true`),
      apiFetch(`/departments?${query}`)
    ])
    if (supRes.ok) suppliers.value = await supRes.json()
    suppliers.value = suppliers.value.filter(s => s.status === 1);
  suppliers.value.sort((a, b) => a.name.localeCompare(b.name))
    if (catRes.ok) categories.value = await catRes.json()
    categories.value = categories.value.filter(c => c.status === 1);
  categories.value.sort((a, b) => a.name.localeCompare(b.name))

    if (itemRes.ok) {
      const itemData = await itemRes.json()
      registeredItems.value = Array.isArray(itemData) ? itemData.filter(i => Number(i?.status) === 1) : []
    }
    if (prodRes.ok) {
      const productData = await prodRes.json()
      products.value = Array.isArray(productData) ? productData.filter(p => Number(p?.status) === 1) : []
    }
    if (depRes.ok) departments.value = await depRes.json()
  } catch (err) {
    console.error('Error loading dropdown data:', err)
  } finally {
    loading.value = false
  }
}

function splitCsvString(s) {
  return String(s || '').split(',').map(x => x.trim()).filter(Boolean)
}

function toBool(v) {
  return v === true || String(v).toLowerCase() === 'true' || Number(v) === 1
}

function splitCsvBool(s) {
  return splitCsvString(s).map(v => toBool(v))
}

async function fetchCurrentBalanceListFor(type, id) {
  const endpoint = type === 'product'
    ? `/products/${id}/stock-balance`
    : `/items/${id}/stock-balance`

  const res = await apiFetch(endpoint)
  if (!res.ok) return []
  const data = await res.json().catch(() => ({}))
  const rows = data?.data
  return Array.isArray(rows) ? rows : []
}

function normKeyPart(n) {
  // Keep consistent matching behavior with `sivPage.vue`
  return Math.round(Number(n ?? 0) * 10000) / 10000
}

function normalizeUnitForCompare(u) {
  return String(u || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
}

function isLiterOrKgUnit(u) {
  const nu = normalizeUnitForCompare(u)
  // Project-specific formatting commonly seen: "K.G." and "liter"
  return (
    nu === 'liter' ||
    nu === 'liters' ||
    nu.startsWith('liter') ||
    nu === 'l' ||
    nu === 'kg' ||
    nu === 'kgs' ||
    nu === 'k.g' ||
    nu === 'k.g.'
  )
}

function getOtherTransferDisplayQuantity(uom, quantityRaw, volumeRaw) {
  // Same pattern as sivPage not-approved / display conversion:
  // liter or K.G. -> (quantity * volume) / 1000
  if (!isLiterOrKgUnit(uom)) return quantityRaw
  const q = Number(quantityRaw)
  const v = Number(volumeRaw)
  if (!Number.isFinite(q) || !Number.isFinite(v) || v === 0) return quantityRaw
  return (q * v) / 1000
}

function getOtherTransferDisplayUnitPrice(uom, unitPriceRaw, volumeRaw) {
  // Same pattern as sivPage getSivDisplayUnitPrice:
  // liter or K.G. -> (price / volume) * 1000
  if (!isLiterOrKgUnit(uom)) return unitPriceRaw
  const p = Number(unitPriceRaw)
  const v = Number(volumeRaw)
  if (!Number.isFinite(p) || !Number.isFinite(v) || v === 0) return unitPriceRaw
  return (p / v) * 1000
}

function ensureOtherTransferMerge(itemRowId) {
  const key = String(itemRowId)
  if (!otherTransferMergeByItemId.value[key]) {
    otherTransferMergeByItemId.value = {
      ...otherTransferMergeByItemId.value,
      [key]: { categoryId: '', selectedItemKey: '' }
    }
  }
  return otherTransferMergeByItemId.value[key]
}

function getOtherTransferMerge(itemRowId) {
  return ensureOtherTransferMerge(itemRowId)
}

function onOtherTransferMergeCategoryChange(itemRowId, categoryId) {
  const key = String(itemRowId)
  otherTransferMergeByItemId.value = {
    ...otherTransferMergeByItemId.value,
    [key]: { categoryId: categoryId || '', selectedItemKey: '' }
  }
}

function onOtherTransferMergeItemChange(itemRowId, selectedItemKey) {
  const key = String(itemRowId)
  const prev = ensureOtherTransferMerge(itemRowId)
  otherTransferMergeByItemId.value = {
    ...otherTransferMergeByItemId.value,
    [key]: { ...prev, selectedItemKey: selectedItemKey || '' }
  }
}

function getOtherTransferMergeSelectableItems(itemRowId) {
  const merge = getOtherTransferMerge(itemRowId)
  const selectedKey = String(merge.selectedItemKey || '')
  const [selectedType = '', selectedIdStr = ''] = selectedKey.split(':')
  const selectedProductId = selectedType === 'product' ? Number(selectedIdStr) : null
  const selectedItemId = selectedType === 'item' ? Number(selectedIdStr) : null
  const hasCategory = !!merge.categoryId
  const catId = hasCategory ? Number(merge.categoryId) : null
  const selectedProduct =
    Number.isFinite(selectedProductId) && selectedProductId > 0
      ? products.value.find(p => Number(p.id) === selectedProductId)
      : null
  const selectedItem =
    Number.isFinite(selectedItemId) && selectedItemId > 0
      ? registeredItems.value.find(i => Number(i.id) === selectedItemId)
      : null

  if (!hasCategory) {
    if (selectedProduct) {
      return [{
        key: `product:${selectedProduct.id}`,
        type: 'product',
        id: selectedProduct.id,
        name: selectedProduct.name,
        unit: selectedProduct.uom ?? selectedProduct.UOM ?? selectedProduct.unit ?? selectedProduct.unitProduction ?? selectedProduct.u_o_m ?? '',
      }]
    }
    if (selectedItem) {
      return [{
        key: `item:${selectedItem.id}`,
        type: 'item',
        id: selectedItem.id,
        name: selectedItem.itemName,
        unit: selectedItem.unit,
      }]
    }
    return []
  }

  const itemOptions = registeredItems.value
    .filter(item => Number(item.categoryId) === catId)
    .map(item => ({
      key: `item:${item.id}`,
      type: 'item',
      id: item.id,
      name: item.itemName,
      unit: item.unit,
    }))

  const activeProducts = products.value.filter(p => (p.status ?? 1) === 1)
  const productsForOptions = selectedProduct
    ? (activeProducts.some(p => Number(p.id) === Number(selectedProduct.id))
        ? activeProducts
        : [...activeProducts, selectedProduct])
    : activeProducts

  const productOptions = productsForOptions
    .filter(p => {
      const pCat = p.categoryId ?? p.category_id ?? p.category?.id ?? null
      return pCat == null ? true : Number(pCat) === catId
    })
    .map(p => ({
      key: `product:${p.id}`,
      type: 'product',
      id: p.id,
      name: p.name,
      unit: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? p.u_o_m ?? '',
    }))

  return [...itemOptions, ...productOptions].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
  )
}

const flattenedOtherTransferPending = computed(() => {
  const flattened = []
  for (const item of otherTransferPendingItems.value) {
    const uom = item?.uom || '-'
    const qStr = item?.quantityDetails
    const pStr = item?.unitPriceDetails
    const vStr = item?.variantDetails

    if (qStr && pStr) {
      // Same comma-separated split pattern as sivPage flattenedSivList
      const qs = String(qStr).split(',').map(s => s.trim())
      const ps = String(pStr).split(',').map(s => s.trim())
      const vs = vStr ? String(vStr).split(',').map(s => s.trim()) : []
      const len = Math.max(qs.length, ps.length, 1)
      for (let i = 0; i < len; i++) {
        const volumeValue = vs[i] ?? vs[0] ?? ''
        const qtyConverted = getOtherTransferDisplayQuantity(uom, qs[i] ?? qs[0] ?? 0, volumeValue)
        const unitPriceConverted = getOtherTransferDisplayUnitPrice(uom, ps[i] ?? ps[0] ?? 0, volumeValue)
        const totalPrice = Number(qtyConverted || 0) * Number(unitPriceConverted || 0)
        flattened.push({
          itemRowId: item.id,
          otherSivId: item.otherSivId,
          sivNo: item.sivNo,
          date: item.date,
          from: item.from,
          itemName: item.itemName,
          uom,
          isPrimaryRow: i === 0,
          groupSize: len,
          displayQuantity: qtyConverted,
          displayUnitPrice: Number.isFinite(Number(unitPriceConverted))
            ? Number(unitPriceConverted).toFixed(2)
            : (unitPriceConverted ?? '-'),
          displayTotalPrice: Number.isFinite(totalPrice) ? totalPrice.toFixed(2) : '0.00'
        })
      }
    } else {
      const v0 = vStr ? String(vStr).split(',')[0]?.trim() : ''
      const qtyConverted = getOtherTransferDisplayQuantity(uom, item?.quantity ?? 0, v0)
      const unitPriceConverted = getOtherTransferDisplayUnitPrice(uom, item?.unitPrice ?? 0, v0)
      const totalPrice = Number(qtyConverted || 0) * Number(unitPriceConverted || 0)
      flattened.push({
        itemRowId: item.id,
        otherSivId: item.otherSivId,
        sivNo: item.sivNo,
        date: item.date,
        from: item.from,
        itemName: item.itemName,
        uom,
        isPrimaryRow: true,
        groupSize: 1,
        displayQuantity: qtyConverted,
        displayUnitPrice: Number.isFinite(Number(unitPriceConverted))
          ? Number(unitPriceConverted).toFixed(2)
          : (unitPriceConverted ?? '-'),
        displayTotalPrice: Number.isFinite(totalPrice) ? totalPrice.toFixed(2) : '0.00'
      })
    }
  }

  // One Approve button per Siv No (rowspan across all split rows of that SIV)
  let i = 0
  while (i < flattened.length) {
    let j = i
    while (j < flattened.length && String(flattened[j].sivNo) === String(flattened[i].sivNo)) j++
    const size = j - i
    for (let k = i; k < j; k++) {
      flattened[k].isSivApprovePrimary = k === i
      flattened[k].sivApproveGroupSize = size
    }
    i = j
  }
  return flattened
})

async function loadOtherTransferPendingItems() {
  try {
    if (!branchId.value) {
      otherTransferPendingItems.value = []
      return
    }
    const res = await apiFetch('/other-siv/transfer-pending')
    if (!res.ok) {
      otherTransferPendingItems.value = []
      return
    }
    const data = await res.json().catch(() => ({}))
    const rows = Array.isArray(data?.data) ? data.data : []
    otherTransferPendingItems.value = rows
    const nextMerge = { ...otherTransferMergeByItemId.value }
    for (const row of rows) {
      const key = String(row.id)
      if (!nextMerge[key]) nextMerge[key] = { categoryId: '', selectedItemKey: '' }
    }
    otherTransferMergeByItemId.value = nextMerge
  } catch (err) {
    console.error('Failed to load Other Transfer pending items', err)
    otherTransferPendingItems.value = []
  }
}

async function approveOtherTransferPending(otherSivId, sivNo) {
  if (isApprovingOtherTransfer.value) return

  const oid = Number(otherSivId)
  if (!Number.isFinite(oid) || oid <= 0) {
    alert('Invalid Other Transfer record.')
    return
  }

  // Unique other_siv_items rows for this Other SIV (Merging Action is per item row)
  const itemRows = []
  const seen = new Set()
  for (const row of otherTransferPendingItems.value) {
    if (Number(row?.otherSivId) !== oid) continue
    const key = String(row.id)
    if (seen.has(key)) continue
    seen.add(key)
    itemRows.push(row)
  }

  if (!itemRows.length) {
    alert('No pending items found for this Other Transfer.')
    return
  }

  const merges = []
  for (const row of itemRows) {
    const merge = getOtherTransferMerge(row.id)
    const selectedKey = String(merge?.selectedItemKey || '').trim()
    if (!merge?.categoryId || !selectedKey) {
      alert(`Please select Merging Action (category and item) for all items of ${sivNo || 'this Other Transfer'} before Approve.`)
      return
    }
    const [type, idStr] = selectedKey.split(':')
    const selectedId = Number(idStr)
    if (!Number.isFinite(selectedId) || selectedId <= 0 || (type !== 'item' && type !== 'product')) {
      alert(`Invalid Merging Action selection for ${sivNo || 'this Other Transfer'}.`)
      return
    }
    merges.push({
      otherSivItemId: row.id,
      itemId: type === 'item' ? selectedId : null,
      productId: type === 'product' ? selectedId : null
    })
  }

  if (!confirm(`Approve Other Transfer ${sivNo || ''}?`)) {
    return
  }

  isApprovingOtherTransfer.value = true
  try {
    const payload = {
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      merges
    }
    const res = await apiFetch(`/other-siv/${oid}/approve-transfer`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert('Error: ' + (data.message || 'Failed to approve Other Transfer'))
      return
    }

    const grvNo = data?.grvNo || ''
    const createdSivNo = data?.sivNo || ''
    alert(
      grvNo || createdSivNo
        ? `Other Transfer approved.`
        : 'Other Transfer approved successfully.'
    )

    // Drop merge state for approved item rows, then reload pending list
    const nextMerge = { ...otherTransferMergeByItemId.value }
    for (const row of itemRows) {
      delete nextMerge[String(row.id)]
    }
    otherTransferMergeByItemId.value = nextMerge
    await loadOtherTransferPendingItems()
    await fetchNextGrvNumberForDisplay()
  } catch (err) {
    console.error('Error approving Other Transfer:', err)
    alert('Critical error approving Other Transfer')
  } finally {
    isApprovingOtherTransfer.value = false
  }
}

function getUnitForBalance(itemType, itemId) {
  if (itemType === 'product') {
    const p = products.value.find(x => Number(x?.id) === Number(itemId))
    return p?.uom ?? ''
  }
  const it = registeredItems.value.find(x => Number(x?.id) === Number(itemId))
  return it?.unit ?? ''
}

function canEditByDetailIndex(detailFlags, fallback, idx) {
  if (!isPendingEditMode.value) return true
  if (Array.isArray(detailFlags) && detailFlags.length > 0) {
    if (detailFlags[idx] !== undefined) return !!detailFlags[idx]
    return !!detailFlags[detailFlags.length - 1]
  }
  return !!fallback
}

function canEditQtyAt(idx) {
  return canEditByDetailIndex(
    pendingEditPermissions.value?.quantityDetails,
    pendingEditPermissions.value?.qty,
    idx
  )
}

function canEditVolumeAt(idx) {
  return canEditByDetailIndex(
    pendingEditPermissions.value?.volumeDetails,
    pendingEditPermissions.value?.volume,
    idx
  )
}

function canEditExpiryAt(idx) {
  return canEditByDetailIndex(
    pendingEditPermissions.value?.expiryDateDetails,
    pendingEditPermissions.value?.expiry,
    idx
  )
}

async function loadPendingEditAccessRows() {
  pendingGrvList.value = []
  pendingFlattenedGrvList.value = []
  try {
    const pendingRes = await apiFetch('/edit-access/grv?status=Pending', { method: 'GET' })
    if (!pendingRes.ok) return
    const pending = await pendingRes.json().catch(() => [])
    if (!Array.isArray(pending) || pending.length === 0) return

    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const grvRes = await apiFetch(`/grv/report?${query}`, { method: 'GET' })
    if (!grvRes.ok) return
    const grvs = await grvRes.json().catch(() => [])
    if (!Array.isArray(grvs) || grvs.length === 0) return

    const grvItemById = new Map()
    for (const g of grvs) {
      const items = Array.isArray(g?.items) ? g.items : []
      for (const gi of items) {
        const gid = Number(gi?.id)
        if (!Number.isFinite(gid)) continue
        grvItemById.set(gid, { grv: g, grvItem: gi })
      }
    }

    const records = []
    for (const req of pending) {
      const matched = grvItemById.get(Number(req?.grvId))
      if (!matched) continue
      const { grv, grvItem } = matched
      const grvCreatorId = Number(grv?.created_by ?? grv?.createdBy ?? grv?.creator?.id ?? 0)
      if (Number(userId.value || 0) !== grvCreatorId) continue
      const requestedItemId = Number(req?.item)
      const grvItemItemId = Number(grvItem?.itemId ?? grvItem?.item_id ?? 0)
      const grvItemProductId = Number(grvItem?.productId ?? grvItem?.product_id ?? 0)
      if (Number.isFinite(requestedItemId) && requestedItemId > 0) {
        const matchesItem = grvItemItemId > 0 && grvItemItemId === requestedItemId
        const matchesProduct = grvItemProductId > 0 && grvItemProductId === requestedItemId
        if (!matchesItem && !matchesProduct) continue
      }

      const itemFromMaster = registeredItems.value.find(i => Number(i.id) === grvItemItemId)
      const productFromMaster = products.value.find(p => Number(p.id) === grvItemProductId)
      const quantityDetails = String(grvItem?.quantityDetails ?? grvItem?.quantity ?? '')
      const unitPriceDetails = String(grvItem?.unitPriceDetails ?? grvItem?.unitPrice ?? '')
      const variantDetails = String(grvItem?.variantDetails ?? grvItem?.remark ?? '')
      const expiryDateDetails = String(grvItem?.expiryDateDetails ?? grvItem?.expiryDate ?? '')
      const quantities = splitCsvString(quantityDetails).map(v => Number(v) || 0)
      const totalQty = quantities.reduce((sum, v) => sum + Number(v || 0), 0)
      const firstUnitPrice = Number(splitCsvString(unitPriceDetails)[0] || 0)
      const lineTotal = splitCsvString(quantityDetails).reduce((sum, qRaw, idx) => {
        const q = Number(qRaw || 0)
        const p = Number(splitCsvString(unitPriceDetails)[idx] ?? splitCsvString(unitPriceDetails)[0] ?? 0)
        return sum + (q * p)
      }, 0)

      records.push({
        id: req?.id ?? null,
        grvItemId: Number(req?.grvId) || null,
        grvItemCreatedAt: grvItem?.created_at ?? grvItem?.createdAt ?? grv?.created_at ?? grv?.createdAt ?? null,
        grvNumber: grv?.grvNo || req?.grvNo || '-',
        transactionDate: grv?.date ? new Date(grv.date).toISOString().slice(0, 10) : todayDate,
        prNumber: grvItem?.prNumber || grv?.prNumber || '',
        supplierId: grv?.supplierId ?? '',
        categoryId:
          itemFromMaster?.categoryId ??
          itemFromMaster?.category_id ??
          productFromMaster?.categoryId ??
          productFromMaster?.category_id ??
          grvItem?.item?.categoryId ??
          grvItem?.item?.category_id ??
          grvItem?.product?.categoryId ??
          grvItem?.product?.category_id ??
          '',
        selectedItemKey: grvItemProductId > 0 ? `product:${grvItemProductId}` : `item:${grvItemItemId}`,
        itemType: grvItemProductId > 0 ? 'product' : 'item',
        itemId: grvItemProductId > 0 ? null : (grvItemItemId || null),
        productId: grvItemProductId > 0 ? grvItemProductId : null,
        quantity: totalQty,
        unitPrice: firstUnitPrice,
        totalPrice: Number(lineTotal || 0),
        expiryDate: splitCsvString(expiryDateDetails)[0] || '',
        isTaxable: !!grvItem?.isTaxable,
        hasReceipt: !!grv?.hasReceipt,
        vat: grvItem?.vat != null ? Number(grvItem.vat) : vatRateForTaxable(!!grvItem?.isTaxable),
        quantityDetails,
        unitPriceDetails,
        variantDetails,
        expiryDateDetails,
        _permissions: {
          transactionDate: toBool(req?.transactionDate),
          prNo: toBool(req?.prNo),
          supplier: toBool(req?.supplier),
          category: toBool(req?.category),
          itemField: toBool(req?.itemField),
          qty: toBool(req?.qty),
          volume: toBool(req?.volume),
          expiry: toBool(req?.expiry),
          taxable: toBool(req?.taxable),
          quantityDetails: splitCsvBool(req?.quantityDetails),
          volumeDetails: splitCsvBool(req?.volumeDetails),
          expiryDateDetails: splitCsvBool(req?.expiryDateDetails),
        },
      })
    }

    pendingGrvList.value = records
    pendingFlattenedGrvList.value = pendingFlatRows.value
  } catch (err) {
    console.error('Error loading pending edit access rows:', err)
  }
}

// Actions
function processGrv() {
  pendingUpdateError.value = ''
  // Sync selection => itemId/productId
  const [selType, selIdStr] = String(form.value.selectedItemKey || '').split(':')
  const selId = Number(selIdStr)

  // Manual validation safeguard
  if (!form.value.supplierId || !form.value.selectedItemKey) {
    alert('Please select Supplier and Item.')
    return
  }
  // Category is required for item-based row, not for product-based row.
  if (selType !== 'product' && !form.value.categoryId) {
    alert('Please select Category for item-based entry.')
    return
  }

  form.value.itemType = selType === 'product' ? 'product' : 'item'
  form.value.itemId = form.value.itemType === 'item' ? selId : null
  form.value.productId = form.value.itemType === 'product' ? selId : null

  // remove rows where everything is empty / zero so we don't send "0,0," noise
  const cleanedEntries = variantEntries.value.filter(e => {
    const q = Number(e.quantity || 0)
    const p = Number(e.unitPrice || 0)
    const v = (e.variant ?? '').toString().trim()
    return q !== 0 || p !== 0 || v !== ''
  })

  const quantities = cleanedEntries.map(e => (e.quantity != null ? Number(e.quantity) || 0 : 0))
  const unitPrices = cleanedEntries.map(e => (e.unitPrice != null ? Number(e.unitPrice) || 0 : 0))
  const variants = cleanedEntries.map(e => (e.variant != null ? e.variant : ''))
  const expiryDates = cleanedEntries.map(e => e.expiryDate || '')

  const totalQty = quantities.reduce((sum, v) => sum + Number(v || 0), 0)

  const record = {
    ...form.value,
    quantity: totalQty,
    unitPrice: unitPrices[0] || 0,
    totalPrice: totalPrice.value,
    quantityDetails: quantities.join(','),
    unitPriceDetails: unitPrices.join(','),
    variantDetails: variants.join(','),
    expiryDateDetails: expiryDates.join(','),
    expiryDate: expiryDates[0] || form.value.expiryDate, // default to first variant or header date
    vat: vatRateForTaxable(!!form.value.isTaxable),
  }

  if (isEditing.value) {
    if (editSource.value === 'items') {
      grvList.value[editIndex.value] = record
    } else if (editSource.value === 'pending') {
      void (async () => {
      const targetId = pendingEditId.value
      let idx = -1
      if (targetId != null && targetId !== '') {
        idx = pendingGrvList.value.findIndex(r => Number(r?.id) === Number(targetId))
      }
      // Fallback for rows without stable id: use captured pending index.
      if (idx === -1 && pendingEditIndex.value >= 0 && pendingEditIndex.value < pendingGrvList.value.length) {
        idx = pendingEditIndex.value
      }
      if (idx !== -1) {
        const oldRecord = pendingGrvList.value[idx]
        const oldQuantities = splitCsvString(oldRecord.quantityDetails ?? oldRecord.quantity).map(v => Number(v) || 0)
        const newQuantities = splitCsvString(record.quantityDetails ?? record.quantity).map(v => Number(v) || 0)
        const oldUnitPrices = splitCsvString(oldRecord.unitPriceDetails ?? oldRecord.unitPrice).map(v => Number(v) || 0)
        const newUnitPrices = splitCsvString(record.unitPriceDetails ?? record.unitPrice).map(v => Number(v) || 0)
        const oldVolumes = splitCsvString(oldRecord.variantDetails ?? oldRecord.variant).map(v => (v ?? '').toString().trim())
        const newVolumes = splitCsvString(record.variantDetails ?? record.variant).map(v => (v ?? '').toString().trim())

        // Fetch current balance list (same endpoint behavior as `sivPage.vue`)
        const itemType = (oldRecord?.itemType || record?.itemType || (oldRecord?.productId ? 'product' : 'item')).toString()
        const itemId = Number(
          itemType === 'product'
            ? (oldRecord?.productId ?? record?.productId ?? record?.product_id)
            : (oldRecord?.itemId ?? record?.itemId ?? record?.item_id)
        )
        let balanceRows = []
        try {
          if (Number.isFinite(itemId) && itemId > 0) {
            balanceRows = await fetchCurrentBalanceListFor(itemType === 'product' ? 'product' : 'item', itemId)
          }
        } catch (e) {
          balanceRows = []
        }

        const unit = getUnitForBalance(itemType === 'product' ? 'product' : 'item', itemId)
        const shouldConvertBalance = isLiterOrKgUnit(unit)

        const hasVariantDetails = (oldVolumes.length > 0 && oldVolumes.some(v => String(v).trim() !== '')) ||
          (newVolumes.length > 0 && newVolumes.some(v => String(v).trim() !== ''))

        // Index balance rows by match key (unitPrice|volume OR unitPrice only)
        const balanceByKey = new Map()
        for (const row of balanceRows) {
          const up = normKeyPart(row?.unitPrice)
          const vol = normKeyPart(row?.volume)
          const key = hasVariantDetails ? `${up}|${vol}` : `${up}`
          if (!balanceByKey.has(key)) balanceByKey.set(key, row)
        }

        const maxLen = Math.max(
          oldQuantities.length,
          newQuantities.length,
          oldUnitPrices.length,
          newUnitPrices.length,
          oldVolumes.length,
          newVolumes.length,
          1
        )
        const lines = []
        let canUpdate = true
        for (let i = 0; i < maxLen; i++) {
          const oq = oldQuantities[i] ?? oldQuantities[0] ?? 0
          const nq = newQuantities[i] ?? newQuantities[0] ?? 0
          const reducedQty = Math.max(0, Number(oq || 0) - Number(nq || 0))
          const up = oldUnitPrices[i] ?? newUnitPrices[i] ?? oldUnitPrices[0] ?? newUnitPrices[0] ?? 0
          const vol = (oldVolumes[i] ?? newVolumes[i] ?? oldVolumes[0] ?? newVolumes[0] ?? '').toString()
          const key = hasVariantDetails ? `${normKeyPart(up)}|${normKeyPart(vol)}` : `${normKeyPart(up)}`
          const match = balanceByKey.get(key)
          const convertedBalance = (() => {
            if (!match) return null
            if (!shouldConvertBalance) return Number(match.balance ?? 0)
            const b = Number(match.balance ?? 0)
            const v = Number(match.volume ?? vol ?? 0)
            if (!Number.isFinite(b) || !Number.isFinite(v) || v === 0) return b
            return (b / v) * 1000
          })()
          const allowed = Number(convertedBalance ?? 0)
          const r = Number(reducedQty || 0)
          const b = Number.isFinite(allowed) && allowed > 0 ? allowed : 0
          if (r > 0 && r > b) {
            canUpdate = false
            lines.push(
              `You cannot reduce qty ${r.toFixed(2)}  which is greater than the current balance ${b.toFixed(2)} with unit price ${Number(up || 0).toFixed(2)} and  volume ${String(vol || '-')}`
            )
          }
        }
        if (!canUpdate) {
          // Fetch recent SIV numbers (>= GRV item createdAt) for the failing unitPrice+volume batches.
          try {
            const fromDate = oldRecord?.grvItemCreatedAt
            if (fromDate) {
              const failingCombos = []
              for (let i = 0; i < maxLen; i++) {
                const oq = oldQuantities[i] ?? oldQuantities[0] ?? 0
                const nq = newQuantities[i] ?? newQuantities[0] ?? 0
                const reducedQty = Math.max(0, Number(oq || 0) - Number(nq || 0))
                if (Number(reducedQty || 0) <= 0) continue

                const up = oldUnitPrices[i] ?? newUnitPrices[i] ?? oldUnitPrices[0] ?? newUnitPrices[0] ?? 0
                const vol = (oldVolumes[i] ?? newVolumes[i] ?? oldVolumes[0] ?? newVolumes[0] ?? '').toString()
                const key = hasVariantDetails ? `${normKeyPart(up)}|${normKeyPart(vol)}` : `${normKeyPart(up)}`
                const match = balanceByKey.get(key)
                const convertedBalance = (() => {
                  if (!match) return null
                  if (!shouldConvertBalance) return Number(match.balance ?? 0)
                  const b = Number(match.balance ?? 0)
                  const v = Number(match.volume ?? vol ?? 0)
                  if (!Number.isFinite(b) || !Number.isFinite(v) || v === 0) return b
                  return (b / v) * 1000
                })()
                const allowed = Number(convertedBalance ?? 0)
                const r = Number(reducedQty || 0)
                const b = Number.isFinite(allowed) && allowed > 0 ? allowed : 0
                if (r > b) {
                  failingCombos.push({
                    unitPrice: up,
                    volume: vol,
                    _mapKey: `${normKeyPart(up)}|${normKeyPart(vol)}`
                  })
                }
              }

              const unique = new Map()
              for (const c of failingCombos) unique.set(c._mapKey, { unitPrice: c.unitPrice, volume: c.volume })

              if (unique.size > 0) {
                const body = {
                  fromDate,
                  combos: Array.from(unique.values()),
                  ...(itemType === 'product' ? { productId: itemId } : { itemId })
                }
                const sRes = await apiFetch('/siv/return-suggestions', { method: 'POST', body: JSON.stringify(body) })
                if (sRes.ok) {
                  const sData = await sRes.json().catch(() => ({}))
                  const suggestions = sData?.suggestions || {}

                  const augmented = lines.map((line) => {
                    const m = line.match(/unit price\s+([0-9.]+)\s+and\s+volume\s+(.+)$/i)
                    if (!m) return line
                    const up = Number(m[1] || 0)
                    const vol = String(m[2] || '').trim()
                    const k = `${normKeyPart(up)}|${normKeyPart(vol)}`
                    const sivNos = Array.isArray(suggestions[k]) ? suggestions[k] : []
                    if (sivNos.length === 0) return line
                    return `${line}\nOr you can return items from SIV Number: ${sivNos.join(', ')}`
                  })

                  pendingUpdateError.value = augmented.join('\n')
                  return
                }
              }
            }
          } catch (e) {
            // fall back to base error lines only
          }

          pendingUpdateError.value = lines.join('\n')
          return
        }

        pendingGrvList.value[idx] = { ...pendingGrvList.value[idx], ...record, id: pendingGrvList.value[idx].id }
        pendingFlattenedGrvList.value = pendingFlatRows.value
        pendingUpdateError.value = ''
        // Persist: update GRV item row and mark edit access as Completed, then reload pending list.
        try {
          const grvItemIdToUpdate = oldRecord?.grvItemId ?? targetId
          if (grvItemIdToUpdate) {
            const updRes = await apiFetch(`/edit-access/grv/${oldRecord?.id}/complete`, {
              method: 'PATCH',
              body: JSON.stringify({
                grvItemId: grvItemIdToUpdate,
                update: {
                  prNumber: record?.prNumber,
                  supplierId: record?.supplierId,
                  transactionDate: record?.transactionDate,
                  itemType: record?.itemType,
                  itemId: record?.itemId,
                  productId: record?.productId,
                  expiryDate: record?.expiryDate,
                  isTaxable: record?.isTaxable,
                  hasReceipt: !!batchHasReceipt.value,
                  vat: vatRateForTaxable(!!record?.isTaxable),
                  quantityDetails: record?.quantityDetails,
                  variantDetails: record?.variantDetails,
                  expiryDateDetails: record?.expiryDateDetails
                }
              })
            })
            if (!updRes?.ok) {
              const errData = await updRes.json().catch(() => ({}))
              throw new Error(errData.message || 'Failed to update GRV in database')
            }
          }
          await loadPendingEditAccessRows()
          pendingUpdateError.value = ''
          alert('Pending GRV updated successfully.')
        } catch (e) {
          pendingUpdateError.value = (e?.message || 'Failed to update GRV in database')
          alert(pendingUpdateError.value)
          return
        }

        isEditing.value = false
        editIndex.value = -1
        editSource.value = null
        pendingEditId.value = null
        pendingEditIndex.value = -1
        pendingEditPermissions.value = null
      }
      })()
      return
    }
    isEditing.value = false
    editIndex.value = -1
    editSource.value = null
    pendingEditId.value = null
    pendingEditIndex.value = -1
    pendingEditPermissions.value = null
  } else {
    grvList.value.unshift(record)
  }

  // Save to localStorage as temporary backup
  localSaveTable()
  
  // Keep header info (GRV No, Supplier, Date) but clear item info
  const savedHeader = {
      grvNumber: form.value.grvNumber,
      transactionDate: form.value.transactionDate,
      supplierId: form.value.supplierId,
      prNumber: form.value.prNumber,
  }
  
  form.value = { 
      ...getInitialForm(), 
      ...savedHeader,
      categoryId: null,
      selectedItemKey: '',
      itemType: 'item',
      itemId: null,
      productId: null,
      quantity: 0,
      unitPrice: 0
  }
  variantEntries.value = [{ quantity: null, unitPrice: null, variant: '', expiryDate: '' }]
}

async function fetchNextGrvNumberForDisplay() {
  try {
    if (!companyId.value && !branchId.value) return
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/grv/next-number?${query}`)
    if (!res?.ok) return
    const data = await res.json().catch(() => ({}))
    const nextNumber = data?.nextNumber || ''
    // Display-only: do not use this value in save/update payloads.
    grvNumberDisplay.value = nextNumber
  } catch (e) {
    console.error('Error fetching GRV number for display:', e)
  }
}

function editGrv(index) {
  const record = grvList.value[index]
  form.value = { ...record }
  pendingUpdateError.value = ''

  try {
    const quantities = (record.quantityDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '')
    const prices = (record.unitPriceDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '')
    const variants = (record.variantDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())
    const expiries = (record.expiryDateDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())

    const maxLen = Math.max(quantities.length, prices.length, variants.length, expiries.length, 1)
    variantEntries.value = []
    for (let i = 0; i < maxLen; i++) {
      variantEntries.value.push({
        quantity: quantities[i] !== undefined ? parseFloat(quantities[i]) || 0 : 0,
        unitPrice: prices[i] !== undefined ? parseFloat(prices[i]) || 0 : 0,
        variant: variants[i] !== undefined ? variants[i] : '',
        expiryDate: expiries[i] !== undefined ? expiries[i] : '',
      })
    }
  } catch (e) {
    variantEntries.value = [{ quantity: record.quantity || null, unitPrice: record.unitPrice || null, variant: record.variant || '', expiryDate: record.expiryDate || '' }]
  }

  isEditing.value = true
  editIndex.value = index
  editSource.value = 'items'
  pendingEditId.value = null
  pendingEditIndex.value = -1
  pendingEditPermissions.value = null
}

function editPendingGrv(index) {
  const record = pendingGrvList.value[index]
  if (!record) return
  batchHasReceipt.value = !!record?.hasReceipt
  form.value = { ...record }
  pendingUpdateError.value = ''

  try {
    const quantities = (record.quantityDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '')
    const prices = (record.unitPriceDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '')
    const variants = (record.variantDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())
    const expiries = (record.expiryDateDetails || '')
      .toString()
      .split(',')
      .map(s => s.trim())

    const maxLen = Math.max(quantities.length, prices.length, variants.length, expiries.length, 1)
    variantEntries.value = []
    for (let i = 0; i < maxLen; i++) {
      variantEntries.value.push({
        quantity: quantities[i] !== undefined ? parseFloat(quantities[i]) || 0 : 0,
        unitPrice: prices[i] !== undefined ? parseFloat(prices[i]) || 0 : 0,
        variant: variants[i] !== undefined ? variants[i] : '',
        expiryDate: expiries[i] !== undefined ? expiries[i] : '',
      })
    }
  } catch (e) {
    variantEntries.value = [{ quantity: record.quantity || null, unitPrice: record.unitPrice || null, variant: '', expiryDate: record.expiryDate || '' }]
  }

  isEditing.value = true
  editIndex.value = index
  editSource.value = 'pending'
  pendingEditId.value = record?.id ?? null
  pendingEditIndex.value = index
  pendingEditPermissions.value = record?._permissions || null
}

function cancelEdit() {
    isEditing.value = false
    editIndex.value = -1
    editSource.value = null
    pendingEditId.value = null
    pendingEditIndex.value = -1
    pendingEditPermissions.value = null
    pendingUpdateError.value = ''
    const savedHeader = {
      grvNumber: form.value.grvNumber,
      transactionDate: form.value.transactionDate,
      supplierId: form.value.supplierId,
      prNumber: form.value.prNumber,
    }
    form.value = { ...getInitialForm(), ...savedHeader }
    variantEntries.value = [{ quantity: null, unitPrice: null, variant: '', expiryDate: '' }]
}

function deleteGrv(index) {
  grvList.value.splice(index, 1)
  localSaveTable()
}

async function saveToDatabase() {
  if (!grvList.value.length) return
  
  isSaving.value = true
  try {
    const payload = {
      date: grvList.value[0].transactionDate,
      supplierId: grvList.value[0].supplierId,
      departmentId: grvList.value[0].departmentId,
      remark: "Batch GRV Import",
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      totalAmount: Number(grandTotal.value || 0),
      hasReceipt: !!batchHasReceipt.value,
      items: grvList.value.map(item => ({
        itemId: item.itemType === 'product' ? null : item.itemId,
        productId: item.itemType === 'product' ? item.productId : null,
        prNumber: item.prNumber || null,
        isTaxable: item.isTaxable === undefined ? true : !!item.isTaxable,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
        remark: item.variantDetails || item.variant,
        quantityDetails: item.quantityDetails || null,
        unitPriceDetails: item.unitPriceDetails || null,
        variantDetails: item.variantDetails || null,
        expiryDate: item.expiryDate ? new Date(item.expiryDate) : null,
        expiryDateDetails: item.expiryDateDetails || null,
      }))
    }

    const res = await apiFetch('/grv', {
      method: 'POST',
      body: JSON.stringify(payload)
    })

      if (res.ok) {
        const responseData = await res.json().catch(() => ({}))
        const savedGrvNo = responseData?.grvNo || responseData?.grv?.grvNo || ''
        if (savedGrvNo) {
          grvList.value = grvList.value.map((row) => ({ ...row, grvNumber: savedGrvNo }))
        }
        alert(savedGrvNo ? `GRV saved successfully. GRV Number: ${savedGrvNo}` : 'GRV saved successfully.')
        
        // Timeout used to guarantee Vue completely rendered DOM changes before opening PDF dialogue 
        setTimeout(async () => {
          const pdfSnapshot = {
            rows: flattenedGrvList.value.map(r => ({ ...r })),
            totals: {
              baseAmount: Number(baseTotal.value || 0),
              taxableAmount: Number(taxableAmountTotal.value || 0),
              tax: Number(taxTotal.value || 0),
              grandTotal: Number(grandTotal.value || 0)
            },
            meta: {
              grvNumber: savedGrvNo || grvList.value?.[0]?.grvNumber || form.value.grvNumber || 'GRV',
              transactionDate: grvList.value?.[0]?.transactionDate || form.value.transactionDate || new Date().toISOString().slice(0, 10)
            }
          }

          if (confirm("Are you sure..........Print?")) {
            await generateGrvPdf(pdfSnapshot)
          }
          
          grvList.value = []
          batchHasReceipt.value = false
          localStorage.removeItem('Mina_grv_temp')
          
          form.value = { ...getInitialForm() }
          variantEntries.value = [{ quantity: null, unitPrice: null, variant: '', expiryDate: '' }]
          // Refresh next GRV number for the next entry.
          await fetchNextGrvNumberForDisplay()
        }, 150)
        
      } else {
        const errorData = await res.json()
        alert('Error: ' + (errorData.message || 'Failed to save GRV'))
      }
    } catch (err) {
      console.error('Error saving to DB:', err)
      alert('Critical error saving to database')
    } finally {
      isSaving.value = false
    }
  }

// Local Storage Helpers
function localSaveTable() {
    localStorage.setItem('Mina_grv_temp', JSON.stringify({
      items: grvList.value,
      hasReceipt: batchHasReceipt.value,
    }))
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl)
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

async function generateGrvPdf(snapshot = null) {
  const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : flattenedGrvList.value
  if (!rows.length) return

  const totals = snapshot?.totals || {
    baseAmount: Number(baseTotal.value || 0),
    taxableAmount: Number(taxableAmountTotal.value || 0),
    tax: Number(taxTotal.value || 0),
    grandTotal: Number(grandTotal.value || 0)
  }
  const meta = snapshot?.meta || {
    grvNumber: rows[0]?.grvNumber || form.value.grvNumber || 'GRV',
    transactionDate: form.value.transactionDate || new Date().toISOString().slice(0, 10)
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const leftMargin = 16
  const rightMargin = 8
  const topMargin = 8
  const headerBottomY = 28
  const totalPagesExp = '{total_pages_count_string}'
  const today = new Date().toLocaleDateString()
  const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(String(printCompanyLogo.value || '').trim())

  const tableBody = rows.map((row, idx) => ([
    String(idx + 1),
    row.grvNumber || '-',
    row.prNumber || '-',
    row.supplierName || '-',
    row.categoryName || '-',
    row.itemName || '-',
    String(row.displayQuantity ?? '-'),
    String(row.displayVariant ?? '-'),
    String(row.displayUnitPrice ?? '-'),
    String(row.displayTotalPrice ?? '-'),
    row.displayExpiry || '-',
    row.isTaxable ? 'Yes' : 'No'
  ]))

  autoTable(doc, {
    head: [[
      '#', 'Grv No', 'Pr No', 'Supplier', 'Category', 'Item',
      'Qty', 'Volume', 'Unit Price', 'Total Price', 'Expiry', 'Taxable'
    ]],
    body: tableBody,
    startY: headerBottomY + 2,
    theme: 'grid',
    margin: { left: leftMargin, right: rightMargin, top: headerBottomY + 2, bottom: 12 },
    styles: {
      fontSize: 9.0,
      cellPadding: 1.2,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    didDrawPage: () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, logoFormat, leftMargin, topMargin, 14, 10, undefined, 'FAST')
      }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 3, { align: 'center' })
      doc.setFontSize(11)
      doc.text('Goods Receiving Voucher', pageWidth / 2, topMargin + 8, { align: 'center' })

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(`Date: ${today}`, pageWidth - rightMargin, topMargin + 6, { align: 'right' })

      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.2)
      doc.line(leftMargin, headerBottomY, pageWidth - rightMargin, headerBottomY)

      const pageNo = doc.getNumberOfPages()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.text(`Page ${pageNo} of ${totalPagesExp}`, pageWidth - rightMargin, pageHeight - 5, { align: 'right' })
    }
  })

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const finalY = (doc.lastAutoTable?.finalY || (headerBottomY + 2)) + 8
  const valueX = pageWidth - rightMargin
  const labelX = valueX - 32
  const lineGap = 6

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Base Amount:', labelX, finalY, { align: 'right' })
  doc.text(Number(totals.baseAmount || 0).toFixed(2), valueX, finalY, { align: 'right' })
  doc.text('Taxable Amount:', labelX, finalY + lineGap, { align: 'right' })
  doc.text(Number(totals.taxableAmount || 0).toFixed(2), valueX, finalY + lineGap, { align: 'right' })
  doc.text('Tax (15%):', labelX, finalY + (lineGap * 2), { align: 'right' })
  doc.text(Number(totals.tax || 0).toFixed(2), valueX, finalY + (lineGap * 2), { align: 'right' })

  doc.setLineWidth(0.2)
  doc.line(labelX - 18, finalY + (lineGap * 2.5), valueX, finalY + (lineGap * 2.5))
  doc.setFont('helvetica', 'bold')
  doc.text('Grand Total:', labelX, finalY + (lineGap * 3.6), { align: 'right' })
  doc.text(Number(totals.grandTotal || 0).toFixed(2), valueX, finalY + (lineGap * 3.6), { align: 'right' })

  const footerY = Math.max(finalY + (lineGap * 7), pageHeight - 28)
  const firstX = leftMargin + 10
  const secondX = pageWidth / 2 - 20
  const thirdX = pageWidth - rightMargin - 56
  const lineW = 40

  doc.setFont('helvetica', 'normal')
  doc.line(firstX, footerY, firstX + lineW, footerY)
  doc.text('Received By', firstX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(secondX, footerY, secondX + lineW, footerY)
  doc.text('Checked By', secondX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(thirdX, footerY, thirdX + lineW, footerY)
  doc.text('Approved By', thirdX + (lineW / 2), footerY + 5, { align: 'center' })

  const safeGrvNo = String(meta.grvNumber || 'GRV').replace(/[\\/:*?"<>|]/g, '-').trim()
  const safeDate = String(meta.transactionDate || new Date().toISOString().slice(0, 10)).replace(/[\\/:*?"<>|]/g, '-').trim()
  const filename = `${safeGrvNo}-${safeDate}.pdf`
  doc.save(filename)
}

function localLoadTable() {
    const saved = localStorage.getItem('Mina_grv_temp')
    if (!saved) return
    try {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed)) {
        grvList.value = parsed
        return
      }
      if (parsed && typeof parsed === 'object') {
        grvList.value = Array.isArray(parsed.items) ? parsed.items : []
        batchHasReceipt.value = !!parsed.hasReceipt
      }
    } catch {
      // ignore invalid local backup
    }
}

function addVariantEntry() {
  variantEntries.value.push({ quantity: null, unitPrice: null, variant: '', expiryDate: '' })
}

function removeVariantEntry(idx) {
  if (variantEntries.value.length <= 1) return
  variantEntries.value.splice(idx, 1)
}

function getSelectableName(type, id) {
  if (type === 'product') {
    const p = products.value.find(p => Number(p.id) === Number(id))
    return p ? (p.name || '-') : '-'
  }
  const it = registeredItems.value.find(i => Number(i.id) === Number(id))
  return it ? (it.itemName || '-') : '-'
}

function getCategoryName(id) {
    const cat = categories.value.find(c => c.id === id)
    return cat ? cat.name : '-'
}



function getSupplierName(id) {
    const s = suppliers.value.find(s => s.id === id)
    return s ? s.name : '-'
}

function contextQueryString() {
  return branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
}

function toggleNoReceiptDropdown() {
  noReceiptDropdownOpen.value = !noReceiptDropdownOpen.value
  if (noReceiptDropdownOpen.value) loadNoReceiptGrvs()
}

async function loadNoReceiptGrvs() {
  if (!companyId.value && !branchId.value) {
    noReceiptGrvList.value = []
    return
  }
  loadingNoReceiptGrvs.value = true
  selectedNoReceiptGrvs.value = []
  try {
    const res = await apiFetch(`/grv/no-receipt?${contextQueryString()}`)
    if (res.ok) {
      const data = await res.json().catch(() => [])
      noReceiptGrvList.value = Array.isArray(data) ? data : []
    } else {
      noReceiptGrvList.value = []
    }
  } catch {
    noReceiptGrvList.value = []
  } finally {
    loadingNoReceiptGrvs.value = false
  }
}

async function saveNoReceiptGrvs() {
  if (!selectedNoReceiptGrvs.value.length) {
    alert('Select at least one GRV.')
    return
  }
  savingNoReceiptGrvs.value = true
  try {
    const res = await apiFetch(`/grv/mark-receipt?${contextQueryString()}`, {
      method: 'PATCH',
      body: JSON.stringify({ grvNumbers: selectedNoReceiptGrvs.value })
    })
    if (res.ok) {
      const data = await res.json().catch(() => ({}))
      alert(`Updated ${data?.count ?? selectedNoReceiptGrvs.value.length} GRV(s) successfully.`)
      noReceiptDropdownOpen.value = false
      selectedNoReceiptGrvs.value = []
      await loadNoReceiptGrvs()
    } else {
      const err = await res.json().catch(() => ({}))
      alert(err?.message || 'Failed to update GRV receipt status.')
    }
  } catch {
    alert('Failed to update GRV receipt status.')
  } finally {
    savingNoReceiptGrvs.value = false
  }
}

function onNoReceiptDocumentClick(e) {
  if (!noReceiptDropdownOpen.value) return
  if (noReceiptDropdownRef.value && !noReceiptDropdownRef.value.contains(e.target)) {
    noReceiptDropdownOpen.value = false
  }
}



onMounted(async () => {
  document.addEventListener('click', onNoReceiptDocumentClick)
  await loadDropdownData()
  await fetchNextGrvNumberForDisplay()
  localLoadTable()
  await loadPendingEditAccessRows()
  await loadOtherTransferPendingItems()
})

onUnmounted(() => {
  document.removeEventListener('click', onNoReceiptDocumentClick)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 12px;
  color: #1e293b;
}

th, td {
  padding: 3px 4px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  font-size: 12px !important;
  color: inherit !important;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

.status-pill {
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: bold;
  display: inline-block;
}
.active  { background: #dcfce7; color: #166534; }
.inactive { background: #f1f5f9; color: red; }

input:disabled,
select:disabled {
  cursor: not-allowed;
  background-color: #f8fafc;
}

input:disabled:hover,
select:disabled:hover {
  cursor: not-allowed;
  border-color: #ef4444;
}

.other-transfer-pending-scroll {
  -webkit-overflow-scrolling: touch;
}

.other-transfer-pending-table {
  width: max-content;
  min-width: 100%;
}

.other-transfer-merge-cell {
  min-width: 20rem;
}

.other-transfer-merge-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.375rem;
  min-width: 18rem;
}

.other-transfer-merge-select {
  flex: 1 1 0;
  min-width: 8rem;
  max-width: 11rem;
}

@media print {
  /* Hide app shell/sidebar/header during GRV print */
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  /* Hide form/actions inside this page */
  .print-hide { display: none !important; }
  
  /* Reset background and stretch for print space */
  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  /* Format table purely for printing layout */
  .print-container {
    box-shadow: none !important;
    border: none !important;
  }
  
  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
  }
  
  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 6px !important;
    text-align: left !important;
    color: #000 !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table {
    border-spacing: 0 !important;
  }
  
  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.delbtn {
  background: none;
  margin-left: 10px;
  border: none;
  color: red;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

</style>